import { Button, addToast, ModalityCodesEnum } from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Col, Form, Row } from 'reactstrap';
import { clean } from 'rut.js';

import { createAssistantRequest } from '../../../../api/requests/sectionsMaintainer';
import { SectionTitle } from '../../../../components/text';
import { PathsLayouts } from '../../../../config/routes';
import { Campus } from '../../../../types/campus';
import { Schedule } from '../../../../types/schedule';
import { SectionDetail } from '../../../../types/sectionMaintainerTypes';
import { formatDate } from '../../../../utils/dates';
import { getSelectedOption } from '../../../../utils/selectFormat';
import ModalCreateAssistant, {
  CreateAssistantForm,
} from '../../../SectionsMaintainer/parts/ModalCreateAssistant';
import SectionTeacherFields from '../../../SectionsMaintainer/parts/SectionTeacherFields';
import { NonStructuredSectionValuesForm } from '../../types/NonStructuredSectionForm.types';
import { NonStructuredSectionEditGeneralFields } from './NonStructuredSectionEditGeneralFields';
import { useNonStructuredSectionEditForm } from './useNonStructuredSectionEditForm';

const FORM_ERRORS = {
  RUT_OR_PASSPORT: 'USER_ALREADY_EXIST',
  EMAIL: 'EMAIL_ALREADY_EXIST',
};

const teacherToUpdateRow = (teacher: SectionDetail['professors'][0]) => {
  return {
    order: teacher.order ?? 0,
    value: teacher.id,
    label: teacher.account?.fullName || '',
    isOnline: !!teacher?.isOnline,
    isPresential: !!teacher?.isPresential,
  };
};

type Props = {
  section: SectionDetail;
  campuses: Campus[];
  schedules: Schedule[];
  deliveryMode: ModalityCodesEnum;
  onSave?: (values: NonStructuredSectionValuesForm) => void | Promise<void>;
};

export function NonStructuredSectionForm(props: Props): JSX.Element {
  const { section, campuses, schedules, onSave, deliveryMode } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { campusOptions, schedulesOptions } = useNonStructuredSectionEditForm(
    campuses,
    schedules,
  );

  const defaultValues: DefaultValues<NonStructuredSectionValuesForm> = useMemo(() => {
    const [code, ...nameParts] = (section.name || '').split('-');
    const name = nameParts.join('-');

    return {
      name: name || '',
      code: code || '',
      quota: section?.maximumStudent?.toString() || '',
      campus: getSelectedOption(section?.campus?.id, campusOptions),
      schedule: section?.schedule
        ? {
            value: section.schedule.id,
            label: section.schedule.name,
          }
        : undefined,
      teachers: section?.professors?.length
        ? section?.professors?.map(teacherToUpdateRow)
        : [
            {
              value: 0,
              label: '',
              isOnline: false,
              isPresential: false,
              order: 0,
            },
          ],
      assistants: section?.assistant?.length
        ? section?.assistant?.map(teacherToUpdateRow)
        : [
            {
              value: 0,
              label: '',
              isOnline: false,
              isPresential: false,
              order: 0,
            },
          ],
    };
  }, [section, campusOptions]);

  const methods = useForm<NonStructuredSectionValuesForm>({
    defaultValues,
  });

  useMemo(() => {
    methods.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  const onSubmit = useCallback(
    async (values: NonStructuredSectionValuesForm) => {
      await onSave?.(values);
    },
    [onSave],
  );

  const createAssistant = useCallback(
    async (values: CreateAssistantForm, reset) => {
      if (values.run) {
        clean(values.run);
      }
      values.birthday = formatDate(values.birthday, 'YYYY-MM-DD');
      const { data, error } = await createAssistantRequest(
        section.id.toString(),
        values,
      );

      if (data && !error) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('common.messages.editSuccess'),
        });
        reset();
        setIsModalOpen(false);
      } else if (
        error &&
        error.data.statusCode === 400 &&
        error.data.message &&
        error.data.message.length > 0
      ) {
        if (error.data.message[0] === FORM_ERRORS.EMAIL) {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t(`sectionsMaintainer.update.createAsistantForm.ErrorEmail`),
          });
        } else if (error.data.message[0] === FORM_ERRORS.RUT_OR_PASSPORT) {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t(
              `sectionsMaintainer.update.createAsistantForm.ErrorRutOrPassport`,
            ),
          });
        } else {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t(`common.errors.save`),
          });
        }
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`common.errors.save`),
        });
      }
    },
    [section.id, setIsModalOpen, t],
  );

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <Row>
            <Col className="mt-3" xs={12}>
              <SectionTitle
                text={t(`sectionsMaintainer.update.generalSection`)}
              />
              <p className="fs-16">
                {t(`sectionsMaintainer.update.generalSectionDescription`)}
              </p>
              <NonStructuredSectionEditGeneralFields
                campusOptions={campusOptions}
                schedulesOptions={schedulesOptions}
              />
            </Col>

            <Col className="mt-3" xs={12}>
              <SectionTitle
                text={t(`sectionsMaintainer.update.teachersSection`)}
              />
              <p className="fs-16">
                {t(`sectionsMaintainer.update.teachersSectionDescription`)}
              </p>
              <SectionTeacherFields
                sectionId={section.id.toString()}
                setIsModalOpen={setIsModalOpen}
                supportsOnline={
                  !!deliveryMode &&
                  (deliveryMode === ModalityCodesEnum.OL ||
                    deliveryMode === ModalityCodesEnum.SP ||
                    deliveryMode === ModalityCodesEnum.DI ||
                    deliveryMode === ModalityCodesEnum.OA)
                }
                supportsPresential={
                  !!deliveryMode &&
                  (deliveryMode === ModalityCodesEnum.PR ||
                    deliveryMode === ModalityCodesEnum.SP ||
                    deliveryMode === ModalityCodesEnum.DU)
                }
              />
            </Col>
          </Row>

          <Row className="py-5 justify-content-end pt-5">
            <Col className="" xs={12} lg={3}>
              <Button
                outlined
                color="primary"
                onClick={() =>
                  history.push(`${PathsLayouts.nonStructured}/sections`)
                }
                className="mr-2"
                fullwidth
                text={t('common.actions.cancel')}
              />
            </Col>
            <Col xs={12} lg={3} className="pb-2 order-1 order-lg-2">
              <Button
                type="submit"
                text={t(`common.actions.save`)}
                loading={methods.formState.isSubmitting}
                fullwidth
              />
            </Col>
          </Row>
        </Form>
      </FormProvider>
      <ModalCreateAssistant
        isModalOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        onSave={createAssistant}
      />
    </>
  );
}
